import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  Button,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { useLocation } from "react-router-dom";

import { headingStyle, xBtn } from "../../style/LapPageStyle";
import { primaryBtnTeal } from "../../style/PatientDetailsStyle";
import { drTableGreen } from "../../style/PatinetFormStyle";
import { textfieldTextBlueBr } from "../../style/PharmacyStyle";
import { showToast } from "../../utils/toastUtil";

import { postPurchaseReturn } from "../../connections/postApi";

const ReturnPurchaseOrder = ({ onClose, refetch }) => {
  const location = useLocation();
  const purchaseOrder = location.state || null;

  const [formState, setFormState] = useState({
    purchase_order_id: purchaseOrder?.id || "",
    reason: "",
    purchase_date: purchaseOrder?.created_at?.split(" ")[0] || "",
    bill_type: purchaseOrder?.bill_type || "",
    bill_number: purchaseOrder?.bill_number || "",
    grand_total: purchaseOrder?.grand_total || "",
  });

  const initialItems = useMemo(() => {
    const items = purchaseOrder?.purchase_items || [];
    return items.map((it) => {
      const rawAvailableStock = it?.available_stock;
      const availableStock =
        rawAvailableStock === null ||
        rawAvailableStock === undefined ||
        rawAvailableStock === ""
          ? null
          : Number(rawAvailableStock);

      let nonReturnReason = "";
      if (availableStock === null) {
        nonReturnReason = "Available quantity data not found";
      } else if (availableStock <= 0) {
        nonReturnReason = "No available quantity";
      }

      return {
        purchase_item_id: it?.id,
        medicine_name: it?.medicine_name || it?.medicine?.name || "",
        batch: it?.batch || "",
        expiry_date: it?.expiry_date || "",
        purchased_qty: Number(it?.quantity || 0),
        available_stock: availableStock,
        pack: it?.pack || "",
        return_qty: 0,
        reason: "",
        isReturnDisabled: !!nonReturnReason,
        nonReturnReason,
      };
    });
  }, [purchaseOrder]);

  const [items, setItems] = useState(initialItems);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const visibleItems = items.filter((it) => {
    const availableStock = it?.available_stock;
    return !(
      availableStock === 0 ||
      availableStock === null ||
      availableStock === undefined ||
      availableStock === ""
    );
  });

  // const hasReturnableItems = items.some((it) => !it.isReturnDisabled);
  const hasReturnableItems = visibleItems.length > 0;

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setFormState((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${field}_${index}`];
      if (field === "reason") delete next[`item_reason_${index}`];
      return next;
    });
  };

  const handleDeleteRow = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.endsWith(`_${index}`)) delete next[k];
      });
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!formState.purchase_order_id) {
      nextErrors.purchase_order_id = "Purchase order id missing";
    }

    if (!formState.reason?.trim()) {
      nextErrors.reason = "Return reason is required";
    }

    const visibleReturnableItems = items.filter(
      (it) =>
        !it.isReturnDisabled &&
        it?.available_stock !== 0 &&
        it?.available_stock !== null &&
        it?.available_stock !== undefined &&
        it?.available_stock !== "",
    );

    if (visibleReturnableItems.length === 0) {
      nextErrors._items = "No returnable items available";
    }

    let anyQtyEntered = false;

    items.forEach((it, idx) => {
      const availableStock = it?.available_stock;

      const isHiddenRow =
        it?.isReturnDisabled ||
        availableStock === 0 ||
        availableStock === null ||
        availableStock === undefined ||
        availableStock === "";

      if (isHiddenRow) return;

      const qty = Number(it.return_qty || 0);

      if (qty > 0) {
        anyQtyEntered = true;
      }

      // qty required for every visible row
      if (!it.return_qty || qty <= 0) {
        nextErrors[`return_qty_${idx}`] = "Return qty is required";
      }

      if (qty < 0) {
        nextErrors[`return_qty_${idx}`] = "Return qty cannot be negative";
      }

      if (qty > Number(availableStock)) {
        nextErrors[`return_qty_${idx}`] =
          `Return quantity cannot be greater than available stock (${availableStock})`;
      }

      // item reason required for every visible row
      if (!it.reason?.trim()) {
        nextErrors[`item_reason_${idx}`] = "Item reason is required";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitReturn = async () => {
    if (!validate()) return;

    setLoading(true);

    const purchase_return_items = items
      .filter((it) => !it.isReturnDisabled && Number(it.return_qty) > 0)
      .map((it) => ({
        purchase_item_id: it.purchase_item_id,
        return_qty: Number(it.return_qty),
        batch: it.batch,
        expiry_date: it.expiry_date,
        reason: it.reason,
      }));

    const payload = {
      purchase_return: {
        purchase_order_id: formState.purchase_order_id,
        reason: formState.reason,
        purchase_return_items,
      },
    };

    try {
      const res = await postPurchaseReturn(payload);
      if (res?.status?.status === "SUCCESS") {
        showToast("Purchase Return created successfully", "SUCCESS");
        refetch?.();
        onClose?.();
      } else {
        showToast(
          res?.status?.message || res?.status?.status || "ERROR",
          "ERROR",
        );
      }
    } catch (e) {
      showToast(e?.message || "Something went wrong", "ERROR");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        height: "90vh",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          scrollbarWidth: "none",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb="10px"
        >
          <Box />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BorderColorIcon
              sx={{
                color: "neutral.navyBlue",
                marginBottom: "4px",
                marginRight: "8px",
              }}
            />
            <Typography
              variant="heading_Large"
              fontWeight={700}
              gutterBottom
              sx={headingStyle}
            >
              Return Purchase Order
            </Typography>
          </Box>
          <IconButton sx={{ marginBottom: "2rem" }} onClick={onClose}>
            <CloseIcon size="small" sx={{ color: "red" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: "10px",
            backgroundColor: "neutral.white",
            border: "1px solid #E6E6E6",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.default_grey" }}>
              Supplier Name
            </Typography>
            <Typography
              variant="body1_text_bold"
              sx={{ color: "neutral.black" }}
            >
              {purchaseOrder?.supplier?.name || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.default_grey" }}>
              Purchase Date
            </Typography>
            <Typography
              variant="body1_text_bold"
              sx={{ color: "neutral.black" }}
            >
              {formState.purchase_date || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.default_grey" }}>
              Bill Type
            </Typography>
            <Typography
              variant="body1_text_bold"
              sx={{ color: "neutral.black", textTransform: "capitalize" }}
            >
              {formState.bill_type || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.default_grey" }}>
              Bill Number
            </Typography>
            <Typography
              variant="body1_text_bold"
              sx={{ color: "neutral.black" }}
            >
              {formState.bill_number || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.default_grey" }}>
              Grand Total
            </Typography>
            <Typography
              variant="body1_text_bold"
              sx={{ color: "neutral.black" }}
            >
              {formState.grand_total !== "" ? `₹${formState.grand_total}` : "-"}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item sm={12}>
            <TextField
              fullWidth
              value={formState.reason}
              name="reason"
              placeholder="Return Reason"
              sx={textfieldTextBlueBr}
              onChange={handleHeaderChange}
              error={!!errors.reason}
              helperText={errors.reason}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <TableContainer sx={{ mt: 2, width: "100%" }}>
            <Table sx={{ width: "100%" }}>
              <TableHead stickyHeader>
                <TableRow
                  sx={{
                    ...drTableGreen,
                    textAlign: "center",
                  }}
                >
                  <TableCell
                    sx={{
                      p: "6px",
                      textAlign: "center",
                      borderTopLeftRadius: 20,
                    }}
                  >
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Medicine
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Batch
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Expiry
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Purchased Qty
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Available Qty
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Return Qty
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: "6px", textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    >
                      Item Reason / Remark
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      p: "6px",
                      textAlign: "center",
                      borderTopRightRadius: 20,
                    }}
                  >
                    <Typography
                      variant="body1_text_bold"
                      sx={{ color: "neutral.black" }}
                    ></Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items?.map((it, index) => {
                  const availableStock = it?.available_stock;

                  if (
                    availableStock === 0 ||
                    availableStock === null ||
                    availableStock === undefined ||
                    availableStock === ""
                  ) {
                    return null;
                  }

                  return (
                    <TableRow key={it.purchase_item_id || index}>
                      <TableCell sx={{ p: "6px" }}>
                        <TextField
                          value={it.medicine_name}
                          sx={textfieldTextBlueBr}
                          inputProps={{ readOnly: true }}
                          placeholder="Medicine"
                          fullWidth
                        />
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        <TextField
                          value={it.batch}
                          sx={textfieldTextBlueBr}
                          inputProps={{ readOnly: true }}
                          placeholder="Batch"
                          fullWidth
                          helperText={it?.pack}
                        />
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        <TextField
                          value={it?.expiry_date}
                          sx={textfieldTextBlueBr}
                          inputProps={{ readOnly: true }}
                          placeholder="Expiry"
                          fullWidth
                        />
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        <TextField
                          value={it?.purchased_qty}
                          sx={textfieldTextBlueBr}
                          inputProps={{ readOnly: true }}
                          placeholder="Purchased"
                          fullWidth
                        />
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        <TextField
                          value={it?.available_stock ?? "-"}
                          sx={textfieldTextBlueBr}
                          inputProps={{ readOnly: true }}
                          placeholder="Available Quantity"
                          fullWidth
                        />
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        {it?.isReturnDisabled ? (
                          <Typography
                            sx={{
                              color: "error.main",
                              fontSize: "13px",
                              textAlign: "center",
                              px: 1,
                            }}
                          >
                            Not returnable
                          </Typography>
                        ) : (
                          <TextField
                            value={it.return_qty}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "return_qty",
                                e.target.value,
                              )
                            }
                            sx={textfieldTextBlueBr}
                            placeholder="Return Qty"
                            type="number"
                            fullWidth
                            error={!!errors[`return_qty_${index}`]}
                            helperText={errors[`return_qty_${index}`] || ""}
                            inputProps={{ min: 0, max: it.available_stock }}
                          />
                        )}
                      </TableCell>

                      <TableCell sx={{ p: "6px" }}>
                        {it.isReturnDisabled ? (
                          <Typography
                            sx={{
                              color: "text.secondary",
                              fontSize: "13px",
                              textAlign: "center",
                              px: 1,
                            }}
                          >
                            {it.nonReturnReason}
                          </Typography>
                        ) : (
                          <TextField
                            value={it.reason}
                            onChange={(e) =>
                              handleItemChange(index, "reason", e.target.value)
                            }
                            sx={textfieldTextBlueBr}
                            placeholder="Item Reason"
                            fullWidth
                            error={!!errors[`item_reason_${index}`]}
                            helperText={errors[`item_reason_${index}`] || ""}
                          />
                        )}
                      </TableCell>

                      <TableCell sx={{ p: "6px", textAlign: "center" }}>
                        <IconButton
                          onClick={() => handleDeleteRow(index)}
                          sx={{ marginLeft: "2px" }}
                        >
                          <CloseIcon
                            sx={{
                              ...xBtn,
                              width: "28px",
                              height: "28px",
                              borderRadius: "50%",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {items.length === 0 && (
                  <TableRow>
                    {" "}
                    <TableCell colSpan={8} sx={{ textAlign: "center", p: 2 }}>
                      {" "}
                      <Typography color="black" variant="heading2">
                        {" "}
                        No items left{" "}
                      </Typography>{" "}
                    </TableCell>{" "}
                  </TableRow>
                )}

                {items?.length > 0 && visibleItems?.length === 0 && (
                  <TableRow>
                    {" "}
                    <TableCell colSpan={8} sx={{ textAlign: "center", p: 2 }}>
                      <Box
                        sx={{
                          mt: 2,
                          py: 6,
                          textAlign: "center",
                          border: "1px solid #E6E6E6",
                          borderRadius: "12px",
                          backgroundColor: "neutral.white",
                        }}
                      >
                        <Typography
                          variant="body1_text_bold"
                          sx={{ color: "neutral.black" }}
                        >
                          No returnable items available
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.default_grey", mt: 1 }}
                        >
                          There are no items with available stock for return.
                        </Typography>
                      </Box>
                    </TableCell>{" "}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!!errors._items && (
            <Typography sx={{ color: "error.main", mt: 1, ml: 1 }}>
              {errors._items}
            </Typography>
          )}
        </Grid>
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        sx={{ zIndex: 10 }}
        marginBottom="5px"
      >
        <Button
          onClick={handleSubmitReturn}
          variant="contained"
          id="submit"
          sx={{
            ...primaryBtnTeal,
            width: "220px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          disabled={loading || !hasReturnableItems}
        >
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <Typography
              textTransform="capitalize"
              color="neutral.white"
              variant="title1_medium"
            >
              Create Return
            </Typography>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ReturnPurchaseOrder;
